import { useCallback, useMemo } from 'react';
import { useGetUserQuery } from '../../Api/attoDeskApi';
import { IUserInfo } from '../../Api/Interface/api.interface';

/**
 * useUser hook — provides privilege checks and user identity helpers.
 */
const useUser = () => {
  const { data, isLoading, isError } = useGetUserQuery();

  const userData = useMemo(() => {
    if (!data?.data) return null;
    return data.data as IUserInfo;
  }, [data]);

  // Check if a user has a specific privilege/permission.
  //
  // Super Admin bypass: any user whose privileges array contains
  // `System_Admin` is treated as having every privilege in the system,
  // regardless of what else the array enumerates. This mirrors the
  // canonical backend rule in PermissionHelper::userHasPermission()
  // ("holders of the System_Admin permission bypass all checks") and
  // in the AttendanceAccess / AssessmentAccess / CheckPermission
  // middlewares which all call `$user->isSuperAdmin()` before running
  // any per-permission comparison.
  //
  // Without this bypass the sidebar and every route guard depend on
  // the login response verbatim listing every granular permission
  // (Student_View, Classes_View, ...) — so a Super Admin whose
  // response returns just `["System_Admin"]` (or an outdated snapshot
  // of them) sees an empty sidebar. The bypass makes the role itself
  // authoritative, matching the backend contract.
  const userHasRight = useCallback(
    (privilege: string) => {
      if (isLoading || isError || !userData) return false;
      if (!Array.isArray(userData.privileges)) return false;
      // Super Admin: allow every permission check unconditionally.
      if (userData.privileges.includes('System_Admin')) return true;
      return userData.privileges.includes(privilege);
    },
    [userData, isLoading, isError]
  );

  // Action-level (CRUD) permission checks. `prefix` is the permission prefix
  // (e.g. "Student"); the permission name is `${prefix}_${Action}`.
  const can = useCallback(
    (prefix: string, action: 'View' | 'Create' | 'Edit' | 'Delete') => userHasRight(`${prefix}_${action}`),
    [userHasRight]
  );
  const canView = useCallback((prefix: string) => can(prefix, 'View'), [can]);
  const canCreate = useCallback((prefix: string) => can(prefix, 'Create'), [can]);
  const canEdit = useCallback((prefix: string) => can(prefix, 'Edit'), [can]);
  const canDelete = useCallback((prefix: string) => can(prefix, 'Delete'), [can]);

  // User type checks
  const isParentUser = useMemo(() => (userData?.user_type ?? 'employee') === 'parent', [userData]);
  const isEmployeeUser = useMemo(() => (userData?.user_type ?? 'employee') === 'employee', [userData]);

  // System-admin signal for portal separation (Staff Portal Phase 1).
  // Derived from the same `System_Admin` permission that the backend's
  // PermissionHelper::isSystemAdmin() checks — no new BE field needed.
  // Used by menu filtering + route guards to distinguish "admin who
  // gets the Parents / Parent Portal Management / Finance items" from
  // "everyone else, including the seeded Staff role".
  const isSystemAdmin = useMemo(() => userHasRight('System_Admin'), [userHasRight]);

  // Canonical "Staff" role signal — matches the roleName the
  // 2026_07_01 Staff seeder creates. A user assigned this role goes
  // to /staff-portal by default (never the Admin dashboard).
  const isStaffRole = useMemo(
    () => (userData?.user_role_name ?? '').toLowerCase() === 'staff',
    [userData]
  );

  // Canonical post-login / drawer-logo home route resolver. One place
  // that decides where a user belongs, so login and every "go home"
  // action stay in sync.
  const homeRoute = useMemo<string>(() => {
    if (isParentUser) return '/parent-portal';
    if (isSystemAdmin) return '/dashboard';
    if (isStaffRole) return '/staff-portal';
    return '/dashboard';
  }, [isParentUser, isSystemAdmin, isStaffRole]);

  // User identity
  const userType = userData?.user_type ?? 'employee';
  const staffId = userData?.staffId ?? null;
  const parentId = userData?.parent_id ?? null;

  return {
    userHasRight,
    can,
    canView,
    canCreate,
    canEdit,
    canDelete,
    isLoading,
    isError,
    isParentUser,
    isEmployeeUser,
    isSystemAdmin,
    isStaffRole,
    homeRoute,
    userType,
    staffId,
    parentId,
    userData,
  };
};

export default useUser;
